// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * Utilidad para resolver códigos de error de auth a claves i18n.
 * Contrato: backend devuelve { success: false, code: "auth.xxx" }; el frontend usa t(getAuthErrorKey(err)).
 *
 * @file utils/authErrors.js
 * @description Mapeo estable backend → frontend → i18n
 */

/** Códigos auth.* reconocidos (solo para validación) */
const AUTH_CODES = new Set([
  'auth.require_email',
  'auth.invalid_credentials',
  'auth.account_blocked',
  'auth.device_limit_reached',
  'auth.account_inactive_email_mismatch',
  'auth.captcha_failed',
  'auth.captcha_expired',
  'auth.captcha_required',
  'auth.unauthorized',
  'auth.generic_error',
  'auth.method_not_allowed',
  'auth.invalid_data',
  'auth.credentials_required',
  'auth.csrf_invalid',
  'auth.username_taken',
  'auth.email_already_exists',
  'auth.username_unavailable',
  'auth.nick_no_spaces',
  'auth.user_not_found',
  'auth.invalid_payload',
  'auth.user_inactive',
  'auth.tenant_mismatch',
  'auth.password_too_short',
  'auth.rate_limit',
  'auth.confirm_success',
  'auth.confirm_already_active',
  'auth.reset_sent',
  'auth.register_success_email_sent',
  'auth.register_success_email_pending',
  'auth.email_send_failed',
  'auth.timeout',
  'auth.email_not_verified',
  'auth.already_verified',
  'auth.resend_sent',
  'auth.reset_token_invalid',
  'auth.reset_token_expired',
  'auth.reset_done',
  'auth.reset_fatal',
  'mail.send_failed'
]);

/**
 * Obtiene la clave i18n para mostrar el error de login.
 * Prioridad: error.code (si es auth.*) → error.data?.code → mapeo de error.message → 'auth.generic_error'.
 *
 * @param {{ code?: string, message?: string, data?: { code?: string, error?: string } }} error
 * @returns {string} Clave de traducción (ej. 'auth.require_email')
 */
export function getAuthErrorKey(error) {
  if (!error || typeof error !== 'object') return 'auth.generic_error';

  const apiErr = error.error ?? error.data?.error;
  if (apiErr === 'INVALID_CREDENTIALS' || apiErr === 'invalid_credentials') return 'auth.invalid_credentials';
  if (apiErr === 'USER_INACTIVE') return 'auth.user_inactive';
  if (apiErr === 'USER_BLOCKED') return 'auth.user_inactive';

  const c = error.code ?? error.data?.code;
  if (typeof c === 'string') {
    if (c === 'AUTH_INVALID_CREDENTIALS') return 'errors.auth_invalid_credentials';
    if (c === 'SAAS_MAX_DEVICES') return 'auth.device_limit_reached';
    if (c === 'auth.email_already_exists') return 'register.emailAlreadyExists';
    if (c.startsWith('auth.') || c === 'mail.send_failed' || AUTH_CODES.has(c)) return c;
    if (c === 'captcha.generate_error') return 'captcha.generateError';
  }

  const msg = typeof error.message === 'string' ? error.message : (error.data?.error ?? '');
  if (!msg) return 'auth.generic_error';

  const s = msg.toLowerCase();
  if (s.includes('requiere') && s.includes('email')) return 'auth.require_email';
  if (s.includes('credenciales inválidas') || s.includes('invalid credentials')) return 'auth.invalid_credentials';
  if (s.includes('cuenta_inactiva') || s.includes('account_blocked')) return 'auth.account_blocked';
  if (s.includes('captcha expirado')) return 'auth.captcha_expired';
  if (s.includes('captcha incorrecto') || s.includes('captcha failed')) return 'auth.captcha_failed';
  if (s.includes('registrado') || (s.includes('username') && s.includes('está'))) return 'auth.username_taken';
  if (s.includes('no está disponible')) return 'auth.username_unavailable';
  if (s.includes('contraseña') && (s.includes('6') || s.includes('caracteres'))) return 'auth.password_too_short';
  if (s.includes('too many') || s.includes('rate')) return 'auth.rate_limit';
  if (s.includes('csrf')) return 'auth.csrf_invalid';
  if (s.includes('método no permitido')) return 'auth.method_not_allowed';
  if (s.includes('datos inválidos')) return 'auth.invalid_data';
  if (s.includes('username') && s.includes('password') && s.includes('requeridos')) return 'auth.credentials_required';
  if (s.includes('cuerpo') && s.includes('inválido')) return 'auth.invalid_payload';
  if (s.includes('invalid payload') || s.includes('invalid_payload')) return 'auth.invalid_payload';
  if (s.includes('usuario no encontrado') || s.includes('user not found')) return 'auth.user_not_found';
  if (s.includes('confirmar tu email') || s.includes('email not verified')) return 'auth.email_not_verified';
  if (s.includes('no pudimos enviar') || s.includes('email_send_failed')) return 'auth.email_send_failed';
  if (s.includes('límite de dispositivos') || s.includes('device limit')) return 'auth.device_limit_reached';

  return 'auth.generic_error';
}
